/**
 * RETOBLOCK SUPABASE CLIENT & CLOUD EXTENSION (V1.1)
 * Quản lý phiên xác thực SSO, đồng bộ dự án .reto lên Supabase Storage và kiểm soát hạn mức 10 dự án.
 */

import { createClient } from '@supabase/supabase-js';

// Global Cross-Tab / Cross-Subdomain Auth Channel
const authChannel = typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined'
  ? new BroadcastChannel('retobots_sso_channel')
  : null;

function isValidHttpUrl(string) {
  if (!string || typeof string !== 'string') return false;
  const trimmed = string.trim();
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) return false;
  if (trimmed.includes('mock.supabase.co')) return false;
  return true;
}

export function getCookieDomain() {
  if (typeof window === 'undefined') return '';
  const hostname = window.location.hostname;
  if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1') return '';
  const parts = hostname.split('.');
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join('.')}`;
  }
  return '';
}

export class SupabaseClientService {
  constructor() {
    const envUrl = import.meta.env?.VITE_SUPABASE_URL;
    const envKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

    if (!isValidHttpUrl(envUrl)) {
      console.error('[CloudStorage] Thiếu biến môi trường VITE_SUPABASE_URL hoặc URL không hợp lệ:', envUrl);
      this.supabaseUrl = '';
      this.supabaseAnonKey = '';
      this.isConfigured = false;
      this.isMockMode = true;
    } else {
      this.supabaseUrl = envUrl.trim();
      this.supabaseAnonKey = (envKey || '').trim();
      this.isConfigured = true;
      this.isMockMode = false;
    }

    this.client = null;
    this.currentUser = null;
    this.authListeners = [];

    this.init();
  }

  init() {
    // Lắng nghe sự kiện đăng xuất từ tab khác
    if (authChannel) {
      authChannel.onmessage = (event) => {
        if (event.data === 'LOGOUT') {
          this.localSignOut(true);
        }
      };
    }

    // Cookie Polling khi người dùng quay lại tab RETOBLOCK
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', () => this.checkGlobalSession());
    }

    // 1. Kiểm tra SSO Token từ URL (?sso_token=... hoặc #sso_token=...) hoặc Cookie chung
    if (typeof window !== 'undefined') {
      try {
        let ssoPayload = null;
        const urlParams = new URLSearchParams(window.location.search);
        let ssoToken = urlParams.get('sso_token');
        if (!ssoToken && window.location.hash) {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          ssoToken = hashParams.get('sso_token');
        }

        if (ssoToken) {
          ssoPayload = JSON.parse(decodeURIComponent(ssoToken));
          // Làm sạch URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          // Kiểm tra Cookie retobots_sso
          const match = document.cookie.match(/(?:^|;\s*)retobots_sso=([^;]+)/);
          if (match && match[1]) {
            ssoPayload = JSON.parse(decodeURIComponent(match[1]));
          }
        }

        if (ssoPayload && (ssoPayload.email || ssoPayload.id)) {
          this.currentUser = {
            id: ssoPayload.id,
            email: ssoPayload.email,
            user_metadata: { full_name: ssoPayload.full_name },
            role: ssoPayload.role || 'trainee'
          };
          localStorage.setItem('retobots_auth_user', JSON.stringify(this.currentUser));
          localStorage.setItem('retoedu_auth_user', JSON.stringify(this.currentUser));
          setTimeout(() => this.notifyAuthChange(this.currentUser, null), 50);
        } else {
          // Thử lấy từ localStorage nếu có
          const saved = localStorage.getItem('retobots_auth_user') || localStorage.getItem('retoedu_auth_user');
          if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed && (parsed.email || parsed.id)) {
              this.currentUser = {
                id: parsed.id,
                email: parsed.email,
                user_metadata: { full_name: parsed.full_name },
                role: parsed.role || 'trainee'
              };
              setTimeout(() => this.notifyAuthChange(this.currentUser, null), 50);
            }
          }
        }
      } catch (e) {
        console.warn('[RETOBLOCK] SSO init notice:', e);
      }
    }

    if (this.isConfigured && this.supabaseUrl) {
      try {
        this.client = createClient(this.supabaseUrl, this.supabaseAnonKey, {
          auth: {
            persistSession: true,
            detectSessionInUrl: true,
            storage: typeof window !== 'undefined' ? window.localStorage : null,
            autoRefreshToken: true
          }
        });

        // Lắng nghe sự kiện xác thực toàn cục
        if (this.client && this.client.auth) {
          this.client.auth.onAuthStateChange(async (event, session) => {
            if (session?.user) {
              this.currentUser = session.user;
              await this.enrichUserWithProfile(this.currentUser);
              this.notifyAuthChange(this.currentUser, session);
            } else {
              this.currentUser = null;
              this.notifyAuthChange(null, null);
            }
          });

          // Khởi tạo phiên hiện thời
          this.client.auth.getSession().then(async ({ data: { session } }) => {
            if (session?.user) {
              this.currentUser = session.user;
              await this.enrichUserWithProfile(this.currentUser);
              this.notifyAuthChange(this.currentUser, session);
            }
          }).catch((err) => {
            console.warn('[RETOBLOCK] getSession notice:', err);
          });
        }
      } catch (e) {
        console.warn('[RETOBLOCK CLOUD] Không thể kết nối Supabase Client (chế độ Offline):', e.message);
        this.isMockMode = true;
        this.isConfigured = false;
      }
    }
  }

  async enrichUserWithProfile(user) {
    if (!user || !user.id || !this.client || this.isMockMode) return user;
    try {
      const { data: profile, error } = await this.client
        .from('profiles')
        .select('role, full_name')
        .eq('id', user.id)
        .maybeSingle();

      if (profile && !error) {
        if (profile.role) user.role = profile.role;
        if (profile.full_name) {
          user.user_metadata = { ...(user.user_metadata || {}), full_name: profile.full_name };
        }
      }
    } catch (err) {
      console.warn('[RETOBLOCK CLOUD] Không thể tải thông tin profile:', err);
    }
    return user;
  }

  onAuthStateChange(callback) {
    this.authListeners.push(callback);
    // Báo ngay trạng thái hiện tại
    callback(this.currentUser);
    return () => {
      this.authListeners = this.authListeners.filter(cb => cb !== callback);
    };
  }

  notifyAuthChange(user, session) {
    for (const listener of this.authListeners) {
      try {
        listener(user, session);
      } catch (err) {
        console.error('[RETOBLOCK CLOUD] Auth listener error:', err);
      }
    }
  }

  isLoggedIn() {
    return !!this.currentUser;
  }

  getUser() {
    return this.currentUser;
  }

  getLoginUrl() {
    const currentOrigin = typeof window !== 'undefined'
      ? window.location.href
      : (import.meta.env?.VITE_RETOBLOCK_URL || 'https://stage-retocode.retobots.vn');
    const baseUrl = import.meta.env?.VITE_RETOZONE_PORTAL_URL || import.meta.env?.VITE_RETOZONE_URL || 'https://stage.retobots.vn';
    return `${baseUrl}/login?redirect_url=${encodeURIComponent(currentOrigin)}`;
  }

  checkGlobalSession() {
    if (typeof window === 'undefined') return;
    const hasLocalAuth = !!(this.currentUser || localStorage.getItem('retobots_auth_user') || localStorage.getItem('retoedu_auth_user'));
    const hasCookie = /(?:^|;\s*)retobots_sso=([^;]+)/.test(document.cookie);
    if (hasLocalAuth && !hasCookie) {
      console.log('[RETOBLOCK] Global SSO cookie removed in another tab. Clearing local auth.');
      this.localSignOut(true);
    }
  }

  async localSignOut(skipBroadcast = false) {
    if (!skipBroadcast && authChannel) {
      try { authChannel.postMessage('LOGOUT'); } catch (e) {}
    }
    if (this.client) {
      try {
        await this.client.auth.signOut();
      } catch (err) {
        console.error('[RETOBLOCK CLOUD] Sign out error:', err);
      }
    }
    this.currentUser = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem('retobots_auth_user');
      localStorage.removeItem('retoedu_auth_user');
      const rootDomain = getCookieDomain();
      if (rootDomain) {
        document.cookie = `retobots_sso=; Domain=${rootDomain}; Path=/; Max-Age=0; SameSite=Lax`;
      }
      document.cookie = 'retobots_sso=; Path=/; Max-Age=0; SameSite=Lax';
    }
    this.notifyAuthChange(null, null);
  }

  async signOut() {
    await this.localSignOut(false);
  }

  /**
   * Đếm số lượng dự án trên đám mây của học viên hiện tại
   */
  async getProjectsCount(userId) {
    const uid = userId || this.currentUser?.id;
    if (!uid || !this.client || this.isMockMode || !this.isConfigured) return 0;

    try {
      const { count, error } = await this.client
        .from('projects')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', uid);

      if (error) {
        console.error('[RETOBLOCK CLOUD] getProjectsCount error:', error);
        throw new Error(`Không thể kiểm tra hạn mức: ${error.message}`);
      }

      return count || 0;
    } catch (err) {
      if (err.message && err.message.includes('Failed to fetch')) {
        throw new Error('Không thể kết nối đến máy chủ lưu trữ (Lỗi mạng hoặc cấu hình Cloudflare/Supabase).');
      }
      throw err;
    }
  }

  /**
   * Lấy danh sách dự án đám mây của học viên
   */
  async listProjects(userId) {
    const uid = userId || this.currentUser?.id;
    if (!uid || !this.client || this.isMockMode || !this.isConfigured) return [];

    try {
      const { data, error } = await this.client
        .from('projects')
        .select('id, user_id, name, language_mode, storage_path, thumbnail_url, created_at, updated_at')
        .eq('user_id', uid)
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('[RETOBLOCK CLOUD] listProjects error:', error);
        throw new Error(`Không thể tải danh sách dự án: ${error.message}`);
      }

      return data || [];
    } catch (err) {
      if (err.message && err.message.includes('Failed to fetch')) {
        throw new Error('Không thể kết nối đến máy chủ lưu trữ (Lỗi mạng hoặc cấu hình Cloudflare/Supabase).');
      }
      throw err;
    }
  }

  /**
   * Lưu hoặc cập nhật dự án .reto lên đám mây kèm kiểm soát hạn mức (Quota <= 10)
   */
  async saveProject({ id, name, languageMode, blob, isUpdate = false }) {
    if (!this.isConfigured || this.isMockMode || !this.client) {
      throw new Error('Cấu hình dịch vụ Đám mây chưa sẵn sàng (Missing VITE_SUPABASE_URL). Vui lòng cấu hình biến môi trường trên hosting.');
    }

    if (!this.currentUser) {
      throw new Error('Yêu cầu đăng nhập tài khoản RETOZONE để lưu dự án lên đám mây.');
    }
    const uid = this.currentUser.id;

    // 1. Kiểm tra hạn mức Client-side (chỉ áp dụng khi lưu mới)
    if (!isUpdate) {
      const currentCount = await this.getProjectsCount(uid);
      if (currentCount >= 10) {
        throw new Error('Hạn mức lưu trữ đám mây đã đầy (10/10 dự án). Vui lòng xóa bớt dự án cũ để tiếp tục.');
      }
    }

    const projectId = id || 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });

    const storagePath = `${uid}/${projectId}.reto`;

    // Chuẩn hóa Upload Payload: kiểm tra đảm bảo fileBody là Blob hoặc Uint8Array hợp lệ
    let fileBody = blob;
    if (!(fileBody instanceof Blob) && !(fileBody instanceof Uint8Array)) {
      if (fileBody instanceof ArrayBuffer) {
        fileBody = new Uint8Array(fileBody);
      } else {
        console.warn('[RETOBLOCK CLOUD] Dữ liệu tải lên không phải Blob/Uint8Array, đang chuyển đổi...');
        fileBody = new Blob([fileBody], { type: 'application/octet-stream' });
      }
    }

    if (fileBody instanceof Blob && (!fileBody.type || fileBody.type === '')) {
      fileBody = fileBody.slice(0, fileBody.size, 'application/octet-stream');
    }

    // 2. Upload tệp .reto vào Supabase Storage Bucket 'reto-projects'
    let uploadResult;
    try {
      uploadResult = await this.client.storage
        .from('reto-projects')
        .upload(storagePath, fileBody, {
          contentType: 'application/octet-stream',
          upsert: true
        });
    } catch (networkErr) {
      console.error('[RETOBLOCK CLOUD] Network/Fetch exception during storage upload:', networkErr);
      const errMsg = networkErr?.message || '';
      if (errMsg.includes('Failed to fetch') || errMsg.includes('NetworkError')) {
        throw new Error('Không thể kết nối đến máy chủ lưu trữ (Lỗi mạng hoặc cấu hình Cloudflare/Supabase).');
      }
      throw new Error(`Lỗi kết nối tải tệp lên đám mây: ${errMsg}`);
    }

    const { error: uploadError } = uploadResult || {};
    if (uploadError) {
      console.error('[RETOBLOCK CLOUD] Chi tiết lỗi upload file:', {
        message: uploadError.message,
        statusCode: uploadError.statusCode,
        error: uploadError
      });
      const statusText = uploadError.statusCode ? ` [Mã lỗi: ${uploadError.statusCode}]` : '';
      if (uploadError.message && uploadError.message.includes('Failed to fetch')) {
        throw new Error('Không thể kết nối đến máy chủ lưu trữ (Lỗi mạng hoặc cấu hình Cloudflare/Supabase).');
      }
      throw new Error(`Lỗi tải tệp lên đám mây: ${uploadError.message}${statusText}`);
    }

    // 3. Upsert thông tin vào bảng public.projects
    const now = new Date().toISOString();
    let dbResult;
    try {
      dbResult = await this.client
        .from('projects')
        .upsert({
          id: projectId,
          user_id: uid,
          name: name || 'Dự án không tên',
          language_mode: languageMode || 'scratch',
          storage_path: storagePath,
          updated_at: now
        })
        .select()
        .single();
    } catch (networkErr) {
      console.error('[RETOBLOCK CLOUD] Lỗi kết nối DB projects:', networkErr);
      const errMsg = networkErr?.message || '';
      if (errMsg.includes('Failed to fetch') || errMsg.includes('NetworkError')) {
        throw new Error('Không thể kết nối đến cơ sở dữ liệu (Lỗi mạng hoặc cấu hình Cloudflare/Supabase).');
      }
      throw new Error(`Lỗi lưu dữ liệu dự án: ${errMsg}`);
    }

    const { data, error: dbError } = dbResult || {};
    if (dbError) {
      console.error('[RETOBLOCK CLOUD] DB upsert error:', dbError);
      throw new Error(`Lỗi lưu dữ liệu dự án: ${dbError.message}`);
    }

    return data;
  }

  /**
   * Tải tệp nhị phân .reto từ Supabase Storage
   */
  async downloadProjectBlob(storagePath) {
    if (!this.client || !this.isConfigured || this.isMockMode) {
      throw new Error('Cấu hình dịch vụ Đám mây chưa sẵn sàng (Missing VITE_SUPABASE_URL).');
    }

    try {
      const { data, error } = await this.client.storage
        .from('reto-projects')
        .download(storagePath);

      if (error) {
        console.error('[RETOBLOCK CLOUD] downloadProjectBlob error:', error);
        throw new Error(`Không thể tải tệp từ đám mây: ${error.message}`);
      }

      return data; // Trả về Blob
    } catch (err) {
      if (err.message && err.message.includes('Failed to fetch')) {
        throw new Error('Không thể kết nối đến máy chủ lưu trữ (Lỗi mạng hoặc cấu hình Cloudflare/Supabase).');
      }
      throw err;
    }
  }

  /**
   * Xóa dự án khỏi đám mây
   */
  async deleteProject(projectId, storagePath) {
    if (!this.currentUser || !this.client || !this.isConfigured || this.isMockMode) {
      throw new Error('Yêu cầu đăng nhập và cấu hình dịch vụ đám mây hợp lệ.');
    }

    // Xóa file khỏi Storage
    if (storagePath) {
      try {
        await this.client.storage.from('reto-projects').remove([storagePath]);
      } catch (e) {
        console.warn('[RETOBLOCK CLOUD] Storage remove warning:', e);
      }
    }

    // Xóa bản ghi trong database
    try {
      const { error } = await this.client
        .from('projects')
        .delete()
        .eq('id', projectId)
        .eq('user_id', this.currentUser.id);

      if (error) {
        console.error('[RETOBLOCK CLOUD] deleteProject error:', error);
        throw new Error(`Lỗi xóa dự án: ${error.message}`);
      }

      return true;
    } catch (err) {
      if (err.message && err.message.includes('Failed to fetch')) {
        throw new Error('Không thể kết nối đến máy chủ lưu trữ (Lỗi mạng hoặc cấu hình Cloudflare/Supabase).');
      }
      throw err;
    }
  }
}

// Export singleton instance
export const supabaseService = new SupabaseClientService();

